import { useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { CalendarClock, Paperclip, Pencil, Trash2, Undo2, Upload } from 'lucide-react'
import { assetFileUrl, getAsset, getAssetSchedule, previewPostDue, updateAsset, uploadAssetFile } from '../../api/operatingCosts'
import { accountLabel, useAccounts } from '../../components/AccountSelect'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { HelpTip } from '../../components/HelpTip'
import { BackLink, DocHeader, StatusBadge } from '../../components/PageParts'
import { useToast } from '../../components/Toast.jsx'
import { fmtDay } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { money, num } from '../sales/salesShared'
import AssetFields, { CAPITALIZATION_SOURCES, assetToForm, checkAsset } from './AssetFields'
import DepreciationReviewModal from './DepreciationReviewModal'
import DisposeAssetModal from './DisposeAssetModal'
import ReverseDepreciationModal from './ReverseDepreciationModal'
import { monthName } from './accountingShared'

const STATUS_TONE = { Active: 'good', Paused: 'warn', Disposed: 'muted' }

// Why depreciation isn't being posted, from the posting preview, in plain
// words; nothing when it simply isn't due yet.
function whyNotDue(reason) {
  const r = String(reason || '')
  const closed = /Next due period (\d{4}-\d{2}) is closed/.exec(r)
  if (closed) return <>Depreciation stops at {monthName(closed[1])}: that month is closed. Reopen it under <Link to={`/accounting/month-end?month=${closed[1]}`}>Month-end close</Link> to post it.</>
  if (r === 'Fully depreciated') return 'Fully depreciated: nothing more to post.'
  if (r.startsWith('Missing')) return 'Depreciation can’t be posted until the accumulated depreciation and expense accounts are set. Edit the asset to choose them.'
  if (r === 'Invalid useful life') return 'Depreciation can’t be posted until the useful life is set. Edit the asset to add it.'
  return ''
}
const back = <BackLink to="/accounting/assets" label="Fixed assets" />

// One fixed asset: its figures, depreciation schedule and details, with
// posting, reversing and disposing.
export default function AssetDetail() {
  const { assetId } = useParams()
  const { notify } = useToast()
  const accounts = useAccounts()
  const fileInput = useRef(null)
  const [asset, setAsset] = useState(null)
  const [schedule, setSchedule] = useState(null)
  const [dueNow, setDueNow] = useState(false)
  const [notDue, setNotDue] = useState('')
  const [error, setError] = useState('')
  const [dialog, setDialog] = useState(null)
  const [form, setForm] = useState(null)
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [showAll, setShowAll] = useState(false)
  const [version, setVersion] = useState(0)
  const reload = () => setVersion((v) => v + 1)

  useEffect(() => {
    setError('')
    Promise.all([getAsset(assetId), getAssetSchedule(assetId)])
      .then(([a, s]) => { setAsset(a); setSchedule(s) })
      .catch((e) => setError(e.message || 'Could not load the asset.'))
    previewPostDue(assetId).then((p) => {
      setDueNow((p?.included || []).length > 0)
      setNotDue((p?.excluded || [])[0]?.reason || '')
    }).catch(() => { setDueNow(false); setNotDue('') })
  }, [assetId, version])

  if (error && !asset) return <div className="ci-form-page"><DocHeader back={back} title="Fixed asset" /><ErrorBanner message={error} /></div>
  if (!asset) return <Spinner label="Loading the asset…" />

  const posted = num(asset.periods_posted)
  const life = num(asset.useful_life_months)
  const locked = posted > 0
  const disposed = asset.status === 'Disposed'
  const monthly = life ? (num(asset.cost) - num(asset.salvage_value)) / life : 0
  const pct = num(asset.cost) > 0 ? Math.min(100, Math.round((num(asset.accumulated_depreciation) / num(asset.cost)) * 100)) : 0
  const lastRun = (schedule?.posted || []).filter((p) => p.status !== 'Reversed').slice(-1)[0]

  const save = async () => {
    const e = checkAsset(form, { periodsPosted: posted, locked })
    setErrors(e)
    if (Object.keys(e).length) { setError('Check the highlighted fields.'); return }
    setSaving(true)
    setError('')
    try {
      const payload = {
        name: form.name.trim(), status: form.status, category: form.category.trim(),
        salvage_value: num(form.salvage_value), useful_life_months: parseInt(form.useful_life_months, 10),
        location_id: form.location_id || null, vendor_id: form.vendor_id || null, custodian_user_id: form.custodian_user_id || null,
        department: form.department.trim(), serial_number: form.serial_number.trim(), model: form.model.trim(),
        manufacturer: form.manufacturer.trim(), capitalization_source: form.capitalization_source,
      }
      if (!locked) {
        Object.assign(payload, {
          cost: num(form.cost), asset_account_code: form.asset_account_code,
          accum_dep_account_code: form.accum_dep_account_code, expense_account_code: form.expense_account_code,
        })
      }
      await updateAsset(assetId, payload)
      notify('Asset saved')
      setForm(null)
      reload()
    } catch (err) {
      setError(err.message || 'Could not save the asset.')
    } finally {
      setSaving(false)
    }
  }

  const upload = async (file) => {
    if (fileInput.current) fileInput.current.value = ''
    if (!file) return
    setUploading(true)
    setError('')
    try {
      const up = await uploadAssetFile(file)
      await updateAsset(assetId, { file_path: up.file_path })
      notify('Document attached')
      reload()
    } catch (err) {
      setError(err.message || 'Could not attach the document.')
    } finally {
      setUploading(false)
    }
  }

  if (form) {
    return (
      <div className="ci-form-page">
        <DocHeader back={back} title={`Edit ${asset.name}`} />
        {error && <ErrorBanner message={error} />}
        <AssetFields form={form} set={(patch) => setForm((f) => ({ ...f, ...patch }))} errors={errors} locked={locked} editing />
        <div className="ci-form-action-bar">
          <div className="ci-form-action-left">
            <button type="button" className="ci-btn ci-btn-primary" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save changes'}</button>
            <button type="button" className="ci-btn ci-btn-ghost" disabled={saving} onClick={() => { setForm(null); setError(''); setErrors({}) }}>Cancel</button>
          </div>
        </div>
      </div>
    )
  }

  // A disposed asset has nothing more scheduled.
  const rows = [...(schedule?.posted || []), ...(disposed ? [] : (schedule?.remaining || []))]
  const firstScheduled = (schedule?.posted || []).length
  const visible = showAll ? rows : rows.slice(0, firstScheduled + 6)
  const source = CAPITALIZATION_SOURCES.find(([v]) => v === asset.capitalization_source)?.[1]
  const details = [
    ['Asset account', accountLabel(accounts, asset.asset_account_code), 'assetAccount'],
    ['Accumulated depreciation', accountLabel(accounts, asset.accum_dep_account_code), 'accumDepAccount'],
    ['Depreciation expense', accountLabel(accounts, asset.expense_account_code), 'depExpenseAccount'],
    ['Residual value', money(asset.salvage_value), 'residualValue'],
    ['Useful life', `${life} months`, 'usefulLife'],
    ['Bought from', asset.vendor_name],
    ['Looked after by', asset.custodian_name],
    ['Department', asset.department],
    ['Serial number', asset.serial_number],
    ['Model', asset.model],
    ['Manufacturer', asset.manufacturer],
    ['How it was paid for', source],
  ].filter(([, v]) => v)

  return (
    <div className="ci-form-page">
      <DocHeader
        back={back}
        title={asset.name}
        badge={<StatusBadge label={asset.status || 'Active'} tone={STATUS_TONE[asset.status] || 'good'} />}
        meta={[`Acquired ${fmtDay(asset.acquisition_date)}`, asset.category, asset.location_name].filter(Boolean).join(' · ')}
        actions={!disposed && (
          <>
            {dueNow && <button data-write type="button" className="ci-btn ci-btn-primary" onClick={() => setDialog('post')}><CalendarClock size={14} />Post depreciation due</button>}
            <button data-write type="button" className="ci-btn ci-btn-ghost" onClick={() => { setForm(assetToForm(asset)); setErrors({}) }}><Pencil size={14} />Edit</button>
            {locked && <button data-write type="button" className="ci-btn ci-btn-ghost" onClick={() => setDialog('reverse')}><Undo2 size={14} />Reverse last month</button>}
            <button data-write type="button" className="ci-btn ci-btn-ghost acc-danger" onClick={() => setDialog('dispose')}><Trash2 size={14} />Dispose</button>
          </>
        )}
      />

      {error && <ErrorBanner message={error} />}
      {!disposed && !dueNow && whyNotDue(notDue) && <p className="doc-hint acc-why-not">{whyNotDue(notDue)}</p>}

      <div className="acc-figures">
        <div><span>Cost</span><strong>{money(asset.cost)}</strong></div>
        <div><span>Depreciated <HelpTip k="accumulatedDepreciation" /></span><strong>{money(asset.accumulated_depreciation)}</strong><small>{pct}% of cost</small></div>
        <div><span>Book value <HelpTip k="netBookValue" /></span><strong>{money(asset.net_book_value)}</strong></div>
        <div><span>Months posted</span><strong>{posted} of {life}</strong><small>{money(monthly)} a month{asset.last_posted_ym ? `, to ${monthName(asset.last_posted_ym)}` : ''}</small></div>
      </div>

      {disposed && (
        <section className="doc-card">
          <div className="doc-card-head"><h2>Disposed <HelpTip k="disposal" /></h2><span>{fmtDay(asset.disposal_date)}</span></div>
          <dl className="acc-details">
            <div><dt>Received</dt><dd>{money(asset.disposal_proceeds)}</dd></div>
            <div><dt>{num(asset.disposal_proceeds) - num(asset.net_book_value) < 0 ? 'Loss' : 'Gain'}</dt><dd>{money(Math.abs(num(asset.disposal_proceeds) - num(asset.net_book_value)))}</dd></div>
            {asset.disposal_notes && <div><dt>Notes</dt><dd>{asset.disposal_notes}</dd></div>}
            {asset.disposal_je_id && <div><dt>Entry</dt><dd><Link to={`/accounting/journal/${asset.disposal_je_id}`}>{docNo('je', asset.disposal_je_no) || 'Open'}</Link></dd></div>}
          </dl>
        </section>
      )}

      <section className="doc-card">
        <div className="doc-card-head">
          <h2>Depreciation schedule <HelpTip k="depreciation" /></h2>
          <span>{rows.length} {rows.length === 1 ? 'month' : 'months'}</span>
        </div>
        {rows.length === 0 ? <p className="doc-empty">Nothing to show.</p> : (
          <div className="doc-table-scroll">
            <table className="ci-line-table acc-view-table">
              <thead><tr><th>Month</th><th className="num">Amount</th><th>Entry</th><th className="center">Status</th></tr></thead>
              <tbody>
                {visible.map((p) => (
                  <tr key={`${p.status}-${p.period_ym}-${p.je_id || ''}`}>
                    <td>{monthName(p.period_ym)}</td>
                    <td className="num">{money(p.amount)}</td>
                    <td>
                      {p.je_id ? <Link to={`/accounting/journal/${p.je_id}`}>{docNo('je', p.je_no) || 'Open'}</Link> : '–'}
                      {p.reversal_je_id && <> · reversed by <Link to={`/accounting/journal/${p.reversal_je_id}`}>{docNo('je', p.reversal_je_no) || 'entry'}</Link></>}
                    </td>
                    <td className="center"><StatusBadge label={p.status} tone={p.status === 'Posted' ? 'good' : (p.status === 'Reversed' ? 'bad' : 'muted')} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {rows.length > visible.length && (
          <div className="acc-show-all"><button type="button" className="acc-link-btn" onClick={() => setShowAll(true)}>Show all {rows.length} months</button></div>
        )}
      </section>

      <section className="doc-card">
        <div className="doc-card-head"><h2>Details</h2></div>
        <dl className="acc-details">
          {details.map(([label, value, k]) => (
            <div key={label}><dt>{label}{k && <HelpTip k={k} />}</dt><dd>{value}</dd></div>
          ))}
          <div>
            <dt>Purchase entry</dt>
            <dd>{asset.je_acquisition_id ? <Link to={`/accounting/journal/${asset.je_acquisition_id}`}>{docNo('je', asset.je_acquisition_no) || 'Open'}</Link> : 'Not posted here'}</dd>
          </div>
          <div>
            <dt>Document</dt>
            <dd className="acc-doc">
              {asset.file_path && <a href={assetFileUrl(asset.asset_id)} target="_blank" rel="noreferrer"><Paperclip size={13} aria-hidden="true" /> Open</a>}
              <input ref={fileInput} type="file" hidden accept=".pdf,image/*" onChange={(e) => upload(e.target.files?.[0])} />
              <button data-write type="button" className="acc-link-btn" disabled={uploading} onClick={() => fileInput.current?.click()}>
                <Upload size={13} aria-hidden="true" /> {uploading ? 'Uploading…' : (asset.file_path ? 'Replace' : 'Attach')}
              </button>
            </dd>
          </div>
        </dl>
      </section>

      <DepreciationReviewModal open={dialog === 'post'} assetId={Number(assetId)} onClose={() => setDialog(null)} onPosted={reload} />
      <DisposeAssetModal open={dialog === 'dispose'} asset={asset} onClose={() => setDialog(null)} onDisposed={reload} />
      <ReverseDepreciationModal open={dialog === 'reverse'} asset={asset} lastRun={lastRun} onClose={() => setDialog(null)} onReversed={reload} />
    </div>
  )
}
